// ============================================================
// FORECAST MATH UTILITIES
// Pure deterministic math functions used across the forecasting pipeline.
//
// These are stateless and side-effect-free — safe to call from any context.
//
// Includes:
//   - seededRandom:  Deterministic pseudo-random number generator (0-1)
//   - normalRandom:  Box-Muller normal distribution sampler
// ============================================================

/**
 * Deterministic pseudo-random number generator.
 *
 * Given the same seed string, always returns the same float in [0, 1).
 * Used throughout the forecast pipeline so that chart jitter is
 * stable across renders (no flicker).
 *
 * Algorithm: Mulberry32-style hash → uniform float.
 */
export const seededRandom = (seed: string): number => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
    const t = h ^ h >>> 16;
    const s = Math.imul(t, 0x21f0aaad);
    const u = s ^ s >>> 15;
    const v = Math.imul(u, 0x735a2d97);
    return ((v ^ v >>> 15) >>> 0) / 4294967296;
};

/**
 * Sample from a standard normal distribution (mean = 0, std = 1).
 *
 * Uses the Box-Muller transform with seeded uniform inputs,
 * so the result is deterministic for a given seed.
 *
 * @param seed  A unique string seed (e.g. "meta-42")
 * @returns     A sample from N(0, 1)
 */
export const normalRandom = (seed: string): number => {
    const u1 = seededRandom(seed + 'u1');
    const u2 = seededRandom(seed + 'u2');
    return Math.sqrt(-2.0 * Math.log(u1 || 0.0001)) * Math.cos(2.0 * Math.PI * u2);
};
